import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Check, FileText, Loader2, ScanText, Trash2, X } from 'lucide-react';
import { apiGet, apiPost, apiDelete } from '@/lib/api';
import { useFocusTarget } from '@/lib/focusTarget';

/**
 * Admin — Question Bank review + evidence entry.
 *
 * Evidence is typed by a human who has actually read the paper. Nothing is
 * inferred, so nothing shown to a student is ever a guess.
 */
const BLANK = {
  board: 'cbse', class_level: '', subject: '', chapter: '', concept_id: '',
  question_text: '', past_paper_count: 1, marks: '', years: '', answer_method: '',
};

export function QuestionBankPanel() {
  const [papers, setPapers] = useState(null);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState('pending_review');
  const [evidence, setEvidence] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [reading, setReading] = useState('');
  const [extraction, setExtraction] = useState(null);
  const [picked, setPicked] = useState(new Set());

  const load = useCallback(async () => {
    try {
      const [p, e] = await Promise.all([
        apiGet(`/admin/question-bank/papers?status=${status}`),
        apiGet('/admin/question-bank/evidence'),
      ]);
      setPapers(p.papers); setCounts(p.counts || {}); setEvidence(e.evidence || []);
    } catch (err) { toast.error(err?.message || 'Could not load the question bank'); setPapers([]); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  // Arriving from a task notification? Jump straight to that paper.
  useFocusTarget(!!papers?.length);

  const review = async (paperId, next) => {
    try {
      await apiPost(`/admin/question-bank/papers/${paperId}/review`, { status: next });
      toast.success(`Paper ${next.replace('_', ' ')}`);
      load();
    } catch (e) { toast.error(e?.message || 'Could not update that paper'); }
  };

  const addEvidence = async () => {
    if (!form.question_text.trim() || !form.subject.trim() || !form.class_level.trim()) {
      return toast.error('Board, class, subject and the question text are all needed');
    }
    setBusy(true);
    try {
      await apiPost('/admin/question-bank/evidence', {
        ...form,
        past_paper_count: Number(form.past_paper_count) || 1,
        marks: form.marks === '' ? null : Number(form.marks),
        years: form.years.split(',').map(y => y.trim()).filter(Boolean),
      });
      toast.success('Evidence recorded — students will see it with the pattern disclaimer');
      setForm(BLANK); load();
    } catch (e) { toast.error(e?.message || 'Could not record that evidence'); }
    setBusy(false);
  };

  const removeEvidence = async (id) => {
    try { await apiDelete(`/admin/question-bank/evidence/${id}`); load(); }
    catch (e) { toast.error(e?.message || 'Could not delete that row'); }
  };

  const readPaper = async (paperId) => {
    setReading(paperId); setExtraction(null);
    try {
      const res = await apiPost(`/admin/question-bank/papers/${paperId}/read`, {});
      if (res.status !== 'proposed' && !res.questions?.length) {
        toast.error(res.message || 'The reader could not find questions in that file');
      } else {
        setExtraction(res);
        setPicked(new Set(res.questions.map(q => q.question_key)));
        toast.success(res.message || `${res.questions.length} questions proposed`);
      }
    } catch (e) { toast.error(e?.message || 'Could not read that paper'); }
    setReading('');
  };

  const confirmExtraction = async () => {
    if (!picked.size) return toast.error('Tick the questions you want to record');
    setBusy(true);
    try {
      const res = await apiPost(`/admin/question-bank/extractions/${extraction.extraction_id}/confirm`,
        { question_keys: [...picked] });
      toast.success(res.message);
      setExtraction(null); setPicked(new Set()); load();
    } catch (e) { toast.error(e?.message || 'Could not record those questions'); }
    setBusy(false);
  };

  return (
    <div className="space-y-5" data-testid="admin-question-bank-panel">
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <h3 className="font-heading text-base font-semibold text-slate-800 flex items-center gap-2">
              <FileText className="w-4 h-4 text-indigo-500" /> Uploaded question papers
            </h3>
            <p className="text-xs text-slate-500 font-body">
              Students upload the paper they sat. Nothing is visible to anyone else until it is approved here.
            </p>
          </div>
          <div className="flex gap-1.5">
            {['pending_review', 'approved', 'rejected', 'all'].map(s => (
              <button key={s} onClick={() => setStatus(s)} data-testid={`qb-admin-filter-${s}`}
                className={`text-[11px] font-heading px-2.5 py-1 rounded-full border ${status === s ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200'}`}>
                {s.replace('_', ' ')}{counts[s] != null ? ` (${counts[s]})` : ''}
              </button>
            ))}
          </div>
        </div>

        {!papers && <p className="mt-3 text-sm text-slate-400 font-body flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>}
        {papers?.length === 0 && <p className="mt-3 text-sm text-slate-500 font-body" data-testid="qb-admin-empty">Nothing in this bucket.</p>}

        <div className="mt-3 space-y-2">
          {(papers || []).map(p => (
            <div key={p.paper_id} className="rounded-xl border border-slate-200 p-3 flex items-start justify-between gap-3" data-testid={`qb-admin-paper-${p.paper_id}`} data-focus-id={p.paper_id}>
              <div className="min-w-0">
                <a href={p.file_url} target="_blank" rel="noreferrer" className="font-heading text-[13px] text-indigo-700 hover:underline">
                  {p.year} · {p.exam_name} · {p.subject}{p.chapter ? ` · ${p.chapter}` : ''}
                </a>
                <p className="font-body text-[11px] text-slate-500">
                  {p.board?.toUpperCase()} · Class {p.class_level}{p.school ? ` · ${p.school}` : ''} · {p.status.replace('_', ' ')}
                </p>
              </div>
              <div className="flex gap-1.5 flex-shrink-0">
                <Button size="sm" variant="outline" className="rounded-xl h-7 text-indigo-600"
                  disabled={reading === p.paper_id}
                  onClick={() => readPaper(p.paper_id)} data-testid={`qb-admin-read-${p.paper_id}`}>
                  {reading === p.paper_id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <ScanText className="w-3 h-3 mr-1" />}
                  Read with AI
                </Button>
                <Button size="sm" variant="outline" className="rounded-xl h-7 text-emerald-600"
                  onClick={() => review(p.paper_id, 'approved')} data-testid={`qb-admin-approve-${p.paper_id}`}>
                  <Check className="w-3 h-3 mr-1" /> Approve
                </Button>
                <Button size="sm" variant="outline" className="rounded-xl h-7 text-rose-600"
                  onClick={() => review(p.paper_id, 'rejected')} data-testid={`qb-admin-reject-${p.paper_id}`}>
                  <X className="w-3 h-3 mr-1" /> Reject
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {extraction && (
        <div className="bg-white rounded-2xl border border-indigo-200 p-5" data-testid="qb-extraction">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <h3 className="font-heading text-base font-semibold text-slate-800 flex items-center gap-2">
                <ScanText className="w-4 h-4 text-indigo-500" /> Proposed questions from this paper
              </h3>
              <p className="text-xs text-slate-500 font-body">
                Read exactly as printed — nothing rewritten, nothing invented. Frequency is counted from the
                distinct papers a question actually appears in, so confirming the same question from another
                year raises its count by one.
              </p>
            </div>
            <div className="flex gap-1.5">
              <Button size="sm" variant="outline" className="rounded-xl h-7"
                onClick={() => setPicked(new Set(extraction.questions.map(q => q.question_key)))}
                data-testid="qb-extraction-select-all">Select all</Button>
              <Button size="sm" variant="outline" className="rounded-xl h-7"
                onClick={() => setPicked(new Set())} data-testid="qb-extraction-clear">Clear</Button>
            </div>
          </div>

          {extraction.detected && (
            <p className="text-[11px] font-body text-slate-500 mt-2" data-testid="qb-extraction-detected">
              Detected on the paper: {[extraction.detected.board, extraction.detected.class_level && `Class ${extraction.detected.class_level}`,
                extraction.detected.subject, extraction.detected.year, extraction.detected.exam_name,
                extraction.detected.total_marks && `${extraction.detected.total_marks} marks`]
                .filter(Boolean).join(' · ') || 'nothing conclusive'}
            </p>
          )}

          <div className="mt-3 space-y-1.5 max-h-[45vh] overflow-y-auto pr-1">
            {extraction.questions.map(q => (
              <label key={q.question_key} data-testid={`qb-extracted-${q.question_key}`}
                className="flex items-start gap-2.5 rounded-xl border border-slate-200 p-2.5 cursor-pointer hover:border-indigo-300 transition-colors">
                <input type="checkbox" checked={picked.has(q.question_key)} className="mt-1"
                  data-testid={`qb-extracted-check-${q.question_key}`}
                  onChange={e => setPicked(s => {
                    const next = new Set(s);
                    if (e.target.checked) next.add(q.question_key); else next.delete(q.question_key);
                    return next;
                  })} />
                <div className="min-w-0">
                  <p className="font-body text-[13px] text-slate-800">{q.number ? `${q.number}. ` : ''}{q.question_text}</p>
                  <p className="font-body text-[11px] text-slate-500">
                    {[q.marks ? `${q.marks} marks` : 'marks not printed', q.section && `Section ${q.section}`,
                      q.question_type, q.chapter_hint].filter(Boolean).join(' · ')}
                  </p>
                </div>
              </label>
            ))}
          </div>

          <Button onClick={confirmExtraction} disabled={busy} className="rounded-xl mt-3" data-testid="qb-extraction-confirm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Record {picked.size} question(s) as evidence
          </Button>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-amber-200 p-5" data-testid="qb-admin-evidence">
        <h3 className="font-heading text-base font-semibold text-slate-800">Record past-paper evidence</h3>
        <p className="text-xs text-slate-500 font-body">
          Type what the paper actually says. Three or more appearances shows as "Frequently asked on this concept";
          4+ marks shows as "High-weightage". Students always see the pattern disclaimer.
        </p>
        <div className="grid sm:grid-cols-3 gap-2.5 mt-3">
          {[['board', 'Board (cbse)'], ['class_level', 'Class (10)'], ['subject', 'Subject'],
            ['chapter', 'Chapter'], ['concept_id', 'Concept id (optional)'], ['past_paper_count', 'Papers it appeared in'],
            ['marks', 'Marks'], ['years', 'Years (comma separated)']].map(([key, label]) => (
            <label key={key} className="block">
              <span className="font-body text-[11px] text-slate-500">{label}</span>
              <Input value={form[key]} onChange={e => setForm(s => ({ ...s, [key]: e.target.value }))}
                className="rounded-xl text-sm mt-0.5" data-testid={`qb-evidence-field-${key}`} />
            </label>
          ))}
        </div>
        <Textarea rows={2} value={form.question_text} onChange={e => setForm(s => ({ ...s, question_text: e.target.value }))}
          placeholder="Paste the question exactly as printed" className="rounded-xl text-sm mt-2.5" data-testid="qb-evidence-question" />
        <Button onClick={addEvidence} disabled={busy} className="rounded-xl mt-2.5" data-testid="qb-evidence-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Record evidence
        </Button>

        <div className="mt-4 space-y-1.5">
          {evidence.map(e => (
            <div key={e.evidence_id} className="rounded-xl border border-slate-200 p-2.5 flex items-start justify-between gap-2" data-testid={`qb-evidence-row-${e.evidence_id}`}>
              <div className="min-w-0">
                <p className="font-body text-[13px] text-slate-800">{e.question_text}</p>
                <p className="font-body text-[11px] text-slate-500">
                  {e.board?.toUpperCase()} · Class {e.class_level} · {e.subject}{e.chapter ? ` · ${e.chapter}` : ''} · {e.label}
                </p>
              </div>
              <button onClick={() => removeEvidence(e.evidence_id)} data-testid={`qb-evidence-delete-${e.evidence_id}`}
                className="text-slate-400 hover:text-rose-600 flex-shrink-0">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default QuestionBankPanel;
